import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Building2, Mail, Crown, User, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuthApi';
import { useOrganization } from '@/hooks/useOrganization';
import {
  getInvitationByToken,
  acceptOrganizationInvitationByToken,
  getOrganization
} from '@/services/organizationService';
import { checkIfEmailExists } from '@/services/userService';
import { OrganizationInvitation, Organization } from '@/types/organization';
import { toast } from '@/hooks/use-toast';
import AuthLayout from '@/components/auth/AuthLayout';

const AcceptInvitationPage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();
  const { refreshOrganizations, switchOrganization } = useOrganization();

  const [invitation, setInvitation] = useState<OrganizationInvitation | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailExists, setEmailExists] = useState<boolean | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);

  useEffect(() => {
    const loadInvitation = async () => {
      console.log('[AcceptInvitationPage] loadInvitation called with token:', token);

      if (!token) {
        console.log('[AcceptInvitationPage] No token provided');
        setError('Invalid invitation link');
        setLoading(false);
        return;
      }

      try {
        console.log('[AcceptInvitationPage] Fetching invitation by token...');
        const foundInvitation = await getInvitationByToken(token);
        console.log('[AcceptInvitationPage] Found invitation:', foundInvitation);

        if (!foundInvitation) {
          console.log('[AcceptInvitationPage] No invitation found for token');
          setError('This invitation link is invalid or has expired');
          setLoading(false);
          return;
        }

        console.log('[AcceptInvitationPage] Setting invitation state');
        setInvitation(foundInvitation);

        // Load organization details
        console.log('[AcceptInvitationPage] Loading organization details...');
        const org = await getOrganization(foundInvitation.organizationId);
        console.log('[AcceptInvitationPage] Found organization:', org);
        setOrganization(org);
      } catch (error) {
        console.error('[AcceptInvitationPage] Error loading invitation:', error);
        setError('Failed to load invitation details');
      } finally {
        console.log('[AcceptInvitationPage] Loading complete');
        setLoading(false);
      }
    };

    loadInvitation();
  }, [token]);

  // Check if invitation email already exists in the system (only when user is not logged in)
  useEffect(() => {
    const checkEmailExists = async () => {
      if (invitation?.email && !user && !authLoading) {
        console.log('[AcceptInvitationPage] Checking if email exists:', invitation.email);
        setCheckingEmail(true);
        try {
          const exists = await checkIfEmailExists(invitation.email);
          console.log('[AcceptInvitationPage] Email exists:', exists);
          setEmailExists(exists);
        } catch (error) {
          console.error('[AcceptInvitationPage] Error checking email:', error);
          setEmailExists(false); // Default to sign-up flow
        } finally {
          setCheckingEmail(false);
        }
      } else if (user) {
        // Reset email checking state if user is logged in
        setEmailExists(null);
        setCheckingEmail(false);
      }
    };

    checkEmailExists();
  }, [invitation?.email, user, authLoading]);

  const handleAcceptInvitation = async () => {
    if (!invitation || !user?.email || !user?.uid) {
      toast({
        title: "Error",
        description: "You must be logged in to accept invitations.",
        variant: "destructive"
      });
      return;
    }

    try {
      setAccepting(true);

      await acceptOrganizationInvitationByToken(invitation.token, user.uid, user.email);

      // Refresh organizations in context
      await refreshOrganizations();

      // Switch to the newly joined organization
      await switchOrganization(invitation.organizationId);

      console.log('[AcceptInvitationPage] Showing success toast');
      toast({
        title: "Welcome to the team!",
        description: `You've successfully joined ${organization?.name}.`,
      });

      console.log('[AcceptInvitationPage] Navigating to organization settings page');
      navigate('/organization-settings');
    } catch (error) {
      console.error('[AcceptInvitationPage] Error accepting invitation:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to accept invitation';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = () => {
    navigate('/');
  };

  const handleSwitchAccounts = async () => {
    try {
      console.log('[AcceptInvitationPage] Switching accounts - logging out current user');
      await logout();

      // Check if invitation email exists to determine which auth page to redirect to
      console.log('[AcceptInvitationPage] Checking if invitation email exists for redirect');
      const emailExists = await checkIfEmailExists(invitation?.email || '');

      if (emailExists) {
        // Email exists - redirect to login with pre-filled email
        navigate(`/login?email=${encodeURIComponent(invitation?.email || '')}&returnTo=${encodeURIComponent(`/invite/${token}`)}`);
      } else {
        // Email doesn't exist - redirect to signup with pre-filled email
        navigate(`/signup?email=${encodeURIComponent(invitation?.email || '')}&token=${token}&returnTo=${encodeURIComponent(`/invite/${token}`)}`);
      }
    } catch (error) {
      console.error('[AcceptInvitationPage] Error switching accounts:', error);
      // Fallback to login page if there's an error
      navigate(`/login?email=${encodeURIComponent(invitation?.email || '')}&returnTo=${encodeURIComponent(`/invite/${token}`)}`);
    }
  };

  // Loading state
  if (authLoading || loading || checkingEmail) {
    return (
      <AuthLayout
        title="Accept Invitation"
        pageTitle="Accept Invitation"
        showTermsLinks={false}
      >
        <div className="space-y-6">
          <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10 animate-pulse">
            <div className="h-12 w-12 rounded-full bg-white/10" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-32 bg-white/10 rounded" />
              <div className="h-3 w-24 bg-white/10 rounded" />
            </div>
          </div>
          <div className="h-12 w-full bg-white/10 rounded-full animate-pulse" />
        </div>
      </AuthLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <AuthLayout
        title="Invalid Invitation"
        pageTitle="Invalid Invitation"
        showTermsLinks={false}
      >
        <div className="space-y-6 text-center">
          <div className="flex justify-center">
            <XCircle className="h-16 w-16 text-red-400" />
          </div>
          <p className="text-[#a4a4a4]">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="w-full h-10 bg-white/10 border border-white/10 text-[#e6e5e5] font-medium rounded-[36px] hover:bg-white/20 transition-colors text-base tracking-[-0.16px]"
          >
            Go Home
          </button>
        </div>
      </AuthLayout>
    );
  }

  // Not found state
  if (!invitation || !organization) {
    return (
      <AuthLayout
        title="Invitation Not Found"
        pageTitle="Invitation Not Found"
        showTermsLinks={false}
      >
        <div className="space-y-6 text-center">
          <div className="flex justify-center">
            <XCircle className="h-16 w-16 text-red-400" />
          </div>
          <p className="text-[#a4a4a4]">This invitation link is invalid or has expired.</p>
          <button
            onClick={() => navigate('/')}
            className="w-full h-10 bg-white/10 border border-white/10 text-[#e6e5e5] font-medium rounded-[36px] hover:bg-white/20 transition-colors text-base tracking-[-0.16px]"
          >
            Go Home
          </button>
        </div>
      </AuthLayout>
    );
  }

  // User not logged in - show smart UI based on email existence
  if (!user) {
    if (emailExists === true) {
      // Email exists - show sign-in option
      return (
        <AuthLayout
          title="Welcome back!"
          pageTitle="Welcome Back"
          showTermsLinks={false}
        >
          <p className="text-center text-[#a4a4a4] text-sm -mt-2 mb-6">
            Sign in to accept your invitation to join {organization.name}
          </p>

          <div className="space-y-6">
            <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10">
              <div className="h-12 w-12 rounded-full bg-white/10 flex items-center justify-center">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-white">{organization.name}</h3>
                <p className="text-sm text-[#a4a4a4]">Organization</p>
              </div>
            </div>

            <div className="text-center">
              <p className="text-sm text-[#a4a4a4] mb-1">
                Sign in to <span className="text-white font-medium">{invitation.email}</span>
              </p>
              <p className="text-xs text-[#a4a4a4]">
                We found an existing account for this email
              </p>
            </div>

            <button
              onClick={() => navigate(`/login?email=${encodeURIComponent(invitation.email)}&returnTo=${encodeURIComponent(`/invite/${token}`)}`)}
              className="w-full h-10 bg-white text-[#404040] font-medium rounded-[36px] hover:bg-white/90 transition-colors text-base tracking-[-0.16px]"
            >
              Sign In to {invitation.email}
            </button>

            <button
              onClick={() => navigate('/')}
              className="w-full h-10 bg-white/10 border border-white/10 text-[#e6e5e5] font-medium rounded-[36px] hover:bg-white/20 transition-colors text-base tracking-[-0.16px]"
            >
              Go Home
            </button>
          </div>
        </AuthLayout>
      );
    } else if (emailExists === false) {
      // Email doesn't exist - show sign-up option
      return (
        <AuthLayout
          title="Create your account"
          pageTitle="Create Account"
          showTermsLinks={false}
        >
          <p className="text-center text-[#a4a4a4] text-sm -mt-2 mb-6">
            Create an account to accept your invitation to join {organization.name}
          </p>

          <div className="space-y-6">
            <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10">
              <div className="h-12 w-12 rounded-full bg-white/10 flex items-center justify-center">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-white">{organization.name}</h3>
                <p className="text-sm text-[#a4a4a4]">Organization</p>
              </div>
            </div>

            <div className="text-center">
              <p className="text-sm text-[#a4a4a4] mb-1">
                Create an account for <span className="text-white font-medium">{invitation.email}</span>
              </p>
              <p className="text-xs text-[#a4a4a4]">
                This email doesn't have an existing account
              </p>
            </div>

            <button
              onClick={() => navigate(`/signup?email=${encodeURIComponent(invitation.email)}&token=${token}&returnTo=${encodeURIComponent(`/invite/${token}`)}`)}
              className="w-full h-10 bg-white text-[#404040] font-medium rounded-[36px] hover:bg-white/90 transition-colors text-base tracking-[-0.16px]"
            >
              Create Account for {invitation.email}
            </button>

            <button
              onClick={() => navigate('/')}
              className="w-full h-10 bg-white/10 border border-white/10 text-[#e6e5e5] font-medium rounded-[36px] hover:bg-white/20 transition-colors text-base tracking-[-0.16px]"
            >
              Go Home
            </button>
          </div>
        </AuthLayout>
      );
    }

    // Fallback only when we don't have invitation data yet or email check failed
    return (
      <AuthLayout
        title="Authentication Required"
        pageTitle="Authentication Required"
        showTermsLinks={false}
      >
        <p className="text-center text-[#a4a4a4] text-sm -mt-2 mb-6">
          You need to sign in or create an account to accept this invitation.
        </p>

        <div className="space-y-4">
          <button
            onClick={() => navigate(`/login?returnTo=${encodeURIComponent(`/invite/${token}`)}`)}
            className="w-full h-10 bg-white text-[#404040] font-medium rounded-[36px] hover:bg-white/90 transition-colors text-base tracking-[-0.16px]"
          >
            Sign In
          </button>

          <button
            onClick={() => navigate(`/signup?token=${token}&returnTo=${encodeURIComponent(`/invite/${token}`)}`)}
            className="w-full h-10 bg-white/10 border border-white/10 text-[#e6e5e5] font-medium rounded-[36px] hover:bg-white/20 transition-colors text-base tracking-[-0.16px]"
          >
            Create Account
          </button>

          <button
            onClick={() => navigate('/')}
            className="w-full h-10 bg-white/10 border border-white/10 text-[#e6e5e5] font-medium rounded-[36px] hover:bg-white/20 transition-colors text-base tracking-[-0.16px]"
          >
            Go Home
          </button>
        </div>
      </AuthLayout>
    );
  }

  // User is logged in - show invitation acceptance UI
  return (
    <AuthLayout
      title="You're Invited!"
      pageTitle="You're Invited"
      showTermsLinks={false}
    >
      <p className="text-center text-[#a4a4a4] text-sm -mt-2 mb-6">
        You've been invited to join an organization.
      </p>

      <div className="space-y-6">
        {/* Organization Info */}
        <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10">
          <div className="h-12 w-12 rounded-full bg-white/10 flex items-center justify-center">
            <Building2 className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-white">{organization.name}</h3>
            <p className="text-sm text-[#a4a4a4]">Organization</p>
          </div>
        </div>

        {/* Invitation Details */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-[#a4a4a4]">
            <Mail className="h-4 w-4" />
            <span>Invited as:</span>
            <Badge
              variant={invitation.role === 'admin' ? 'default' : 'secondary'}
              className={invitation.role === 'admin'
                ? 'bg-white text-[#404040]'
                : 'bg-white/10 text-white border-none'
              }
            >
              {invitation.role === 'admin' ? (
                <>
                  <Crown className="h-3 w-3 mr-1" />
                  Admin
                </>
              ) : (
                <>
                  <User className="h-3 w-3 mr-1" />
                  Member
                </>
              )}
            </Badge>
          </div>

          <div className="text-sm text-[#a4a4a4]">
            <p>
              {invitation.role === 'admin'
                ? 'Admins can manage organization settings and invite other members.'
                : 'Members can access organization data and create content.'}
            </p>
          </div>
        </div>

        {/* Email mismatch warning */}
        {user.email !== invitation.email && (
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-3 bg-yellow-500/20 border border-yellow-500/50 rounded-xl"
            >
              <div className="flex items-center gap-2 text-sm text-yellow-200">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>
                  This invitation was sent to <strong>{invitation.email}</strong> but you're signed in as <strong>{user.email}</strong>.
                </span>
              </div>
            </motion.div>
          </AnimatePresence>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          {user.email !== invitation.email ? (
            // Email mismatch - show Switch Accounts option
            <button
              onClick={handleSwitchAccounts}
              disabled={accepting}
              className="w-full h-10 bg-white text-[#404040] font-medium rounded-[36px] hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-base tracking-[-0.16px]"
            >
              <User className="h-4 w-4" />
              Switch Accounts
            </button>
          ) : (
            // Email match - show normal Accept/Decline options
            <>
              <button
                onClick={handleAcceptInvitation}
                disabled={accepting}
                className="w-full h-10 bg-white text-[#404040] font-medium rounded-[36px] hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-base tracking-[-0.16px]"
              >
                {accepting ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                      <svg
                        className="h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    </motion.div>
                    Joining...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Accept Invitation
                  </>
                )}
              </button>
              <button
                onClick={handleDecline}
                disabled={accepting}
                className="w-full h-10 bg-white/10 border border-white/10 text-[#e6e5e5] font-medium rounded-[36px] hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-base tracking-[-0.16px]"
              >
                Decline
              </button>
            </>
          )}
        </div>
      </div>
    </AuthLayout>
  );
};

export default AcceptInvitationPage;
